// src/hooks/useRegions.ts
import {useState, useCallback, useMemo} from "react";
import {
  useGetRegionsQuery,
  useGetRegionsDropdownQuery,
  useGetRegionByIdQuery,
  useCreateRegionMutation,
  useUpdateRegionMutation,
  useDeleteRegionMutation,
  useBulkCreateRegionsMutation,
  useGetRegionStatsQuery,
} from "../store/api/regionsApi";
import {useToast} from "./common/useToast";
import {useConfirm} from "./common/useConfirm";
import type {
  RegionFilterDto,
  CreateRegionDto,
  UpdateRegionDto,
} from "../types/region";

export const useRegions = (filters: RegionFilterDto = {}) => {
  const {success, error} = useToast();
  const {confirm} = useConfirm();

  // Query hooks
  const {
    data: regionsResponse,
    isLoading,
    isFetching,
    error: fetchError,
    refetch,
  } = useGetRegionsQuery(filters);

  const {data: dropdownRegions, isLoading: dropdownLoading} =
    useGetRegionsDropdownQuery();

  const {data: statsData, isLoading: statsLoading} = useGetRegionStatsQuery();

  // Mutation hooks
  const [createRegion, {isLoading: isCreating}] = useCreateRegionMutation();
  const [updateRegion, {isLoading: isUpdating}] = useUpdateRegionMutation();
  const [deleteRegion, {isLoading: isDeleting}] = useDeleteRegionMutation();
  const [bulkCreateRegions, {isLoading: isBulkCreating}] =
    useBulkCreateRegionsMutation();

  // Memoized values
  const regions = useMemo(() => regionsResponse?.data || [], [regionsResponse]);
  const pagination = useMemo(() => regionsResponse?.meta, [regionsResponse]);
  const stats = useMemo(
    () => regionsResponse?.stats || statsData,
    [regionsResponse, statsData]
  );

  // Handlers
  const handleCreate = useCallback(
    async (data: CreateRegionDto) => {
      try {
        await createRegion(data).unwrap();
        success("Success", "Region created successfully");
        return true;
      } catch (err) {
        error("Error", "Failed to create region");
        return false;
      }
    },
    [createRegion, success, error]
  );

  const handleUpdate = useCallback(
    async (id: number, data: UpdateRegionDto) => {
      try {
        await updateRegion({id, data}).unwrap();
        success("Success", "Region updated successfully");
        return true;
      } catch (err) {
        error("Error", "Failed to update region");
        return false;
      }
    },
    [updateRegion, success, error]
  );

  const handleDelete = useCallback(
    async (id: number) => {
      const confirmed = await confirm({
        title: "Delete Region",
        message:
          "Are you sure you want to delete this region? All associated townships and properties will be affected.",
        variant: "danger",
        confirmText: "Delete",
      });

      if (!confirmed) return false;

      try {
        await deleteRegion(id).unwrap();
        success("Success", "Region deleted successfully");
        return true;
      } catch (err) {
        error("Error", "Failed to delete region");
        return false;
      }
    },
    [deleteRegion, confirm, success, error]
  );

  const handleBulkCreate = useCallback(
    async (regionsData: CreateRegionDto[]) => {
      try {
        await bulkCreateRegions({regions: regionsData}).unwrap();
        success(
          "Success",
          `${regionsData.length} regions created successfully`
        );
        return true;
      } catch (err) {
        error("Error", "Failed to create regions");
        return false;
      }
    },
    [bulkCreateRegions, success, error]
  );

  return {
    // Data
    regions,
    dropdownRegions: dropdownRegions || [],
    pagination,
    stats,

    // Loading states
    isLoading: isLoading || isFetching,
    dropdownLoading,
    statsLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isBulkCreating,

    // Error
    error: fetchError,

    // Actions
    refetch,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleBulkCreate,
  };
};

// Hook for single region
export const useRegion = (id: number | undefined) => {
  const {
    data: region,
    isLoading,
    error,
    refetch,
  } = useGetRegionByIdQuery(id!, {
    skip: !id,
  });

  return {
    region,
    isLoading,
    error,
    refetch,
  };
};
